import { Ref, ref, computed } from 'vue';
import pMap from 'p-map';

import { CustomerMgmtTypes, useSearchUsers } from '@ilteducation/customer-mgmt';
import { checkUserUpdatePermission } from '@ilteducation/customer-mgmt-permissions';

import useModal, { modals } from '../../composables/useModal';
import { usePermissions } from '../../composables/usePermissions';
const usersToCreateLoginCard = ref<CustomerMgmtTypes.SearchUserOutput[]>([]);
const usersNotAllowedToUpdate: Ref<Record<string, string[]>> = ref({});
const isUsersLoading = ref(false);
const isUsersLoadingForMultipleGroups = ref(false);
export function useCreateLoginCards() {
  const { fetchAllGroupMemebers } = useSearchUsers();
  const { open: openCreateMultipleLoginCardsModal } = useModal(modals.CREATE_MULTIPLE_LOGIN_CARDS);

  const {
    permissionContext,
    getCallerAssociations,
    getGroupMemberships,
    createGetTargetAssociations,
    reporterWithReason,
  } = usePermissions();

  const checkUserPropertiesEditable = (
    user: CustomerMgmtTypes.SearchUserOutput,
    propertiesToCheck: Partial<CustomerMgmtTypes.UpdateUserRequest>,
  ): Promise<ReturnType<typeof reporterWithReason>> => {
    if (!user) {
      return Promise.resolve({ allowed: false, reason: 'not_permitted' });
    }

    return checkUserUpdatePermission(
      permissionContext.value,
      {
        userId: user.uid,
        ...propertiesToCheck,
      },
      getCallerAssociations,
      createGetTargetAssociations(user),
      getGroupMemberships,
      reporterWithReason,
    );
  };

  const fetchGroupUsersTocreateLoginCards = async (groupUid: string) => {
    if (!groupUid) {
      return [];
    }

    isUsersLoading.value = true;
    const groupUsers = await fetchAllGroupMemebers(groupUid);

    const editableUsers = await pMap(
      groupUsers,
      async (u) => {
        const isUserUpdateAllowed = await checkUserPropertiesEditable(u, {
          qrToken: 'tempPassword',
          password: 'tempPassword',
        });

        if (isUserUpdateAllowed.allowed) {
          return u;
        }

        usersNotAllowedToUpdate.value[isUserUpdateAllowed.reason] =
          usersNotAllowedToUpdate.value[isUserUpdateAllowed.reason] || [];
        usersNotAllowedToUpdate.value[isUserUpdateAllowed.reason].push(u.uid);
        return null;
      },
      { concurrency: 5 },
    );

    usersToCreateLoginCard.value = editableUsers.filter((u) => u !== null) as CustomerMgmtTypes.SearchUserOutput[];
    isUsersLoading.value = false;

    return usersToCreateLoginCard.value;
  };

  type ModalContextType = Parameters<typeof openCreateMultipleLoginCardsModal>[0];
  const createLoginCardsForGroupUsers = (groupUid: string, modalContext?: ModalContextType) => {
    fetchGroupUsersTocreateLoginCards(groupUid);
    openCreateMultipleLoginCardsModal(modalContext);
  };

  const createLoginCardsForMultipleGroupUsers = async (groupUids: string[], modalContext?: ModalContextType) => {
    if (!groupUids || groupUids.length === 0) {
      return;
    }
    isUsersLoadingForMultipleGroups.value = true;
    usersNotAllowedToUpdate.value = {};
    usersToCreateLoginCard.value = [];
    openCreateMultipleLoginCardsModal(modalContext);
    const usersByUid: Record<string, CustomerMgmtTypes.SearchUserOutput> = {};
    await pMap(
      groupUids,
      async (groupUid) => {
        const usersInThisGroup = await fetchGroupUsersTocreateLoginCards(groupUid);
        usersInThisGroup.forEach((u) => {
          usersByUid[u.uid] = u;
        });
      },
      { concurrency: 3 },
    );
    usersToCreateLoginCard.value = Object.values(usersByUid);
    isUsersLoadingForMultipleGroups.value = false;
  };

  const reset = () => {
    usersToCreateLoginCard.value = [];
    isUsersLoading.value = false;
    // @ts-ignore
    usersNotAllowedToUpdate.value = {} as Record<string, string[]>;
  };

  return {
    usersToCreateLoginCard,
    usersNotAllowedToUpdate,
    isUsersLoading: computed(() => isUsersLoading.value || isUsersLoadingForMultipleGroups.value),
    fetchGroupUsersTocreateLoginCards,
    createLoginCardsForGroupUsers,
    createLoginCardsForMultipleGroupUsers,
    reset,
  };
}
